/**
 * The board and its frame (design L2 "Board material": 12 px of frame around
 * an 8 px-radius board, the frame carrying sh2 + frameEdge).
 *
 * TWO rules from UX spec §6a live here:
 *  1. the Chessboard ELEMENT is memoised on the handful of props that really
 *     change, so a clock tick or a Rail state swap cannot make chessground
 *     re-diff;
 *  2. the countdown overlay is a SIBLING of the board, never a child, so the
 *     board's DOM is never re-parented and chessground keeps its canvas state.
 *
 * `@/Components/Chessboard` is an ALLOWED exception to the View import list of
 * unit convention §1: the board cannot live anywhere else.
 *
 * While the player is browsing an earlier ply of their own live game, a
 * transparent `Pressable` covers the board square and snaps back to the live
 * position on any click — the old screen's `illegalTouchPieceCount` jump. It sits
 * INSIDE the board view, so the Rail's move list keeps its own keyboard and
 * scroll browsing.
 */
import React, {useMemo} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

// The board resolves per platform (index.web.tsx / index.native.tsx), which
// tsc's node resolution cannot see (there is no index.ts). Same import the
// other board screens use — see FunChallenges/PuzzleRoyaleBattle.tsx.
// @ts-ignore
import ChessboardWithArrow from '@/Components/Chessboard';

import {RADIUS} from '../../theme/tokens';
import {useV2Theme} from '../../theme/useV2Theme';
import BoardCountdown from '../BoardCountdown';
import {BoardFrameModel} from './model';

/** chessground's own animation; the board must feel instant (UX spec §7) */
const ANIMATION_MS = 10;

export default React.memo(function BoardFrameView({
  board,
  orientation,
  boardSize,
  padX,
  padY,
  move,
  restrictPiece,
  browsing,
  snapToLive,
}: BoardFrameModel) {
  const tokens = useV2Theme(s => s.tokens);

  const boardElement = useMemo(
    () => (
      <ChessboardWithArrow
        position={board.position}
        boardOrientation={orientation}
        disabled={board.disabled}
        lastMoveFen={board.lastMoveFen}
        arePremovesAllowed={board.arePremovesAllowed}
        restrictedPiece={board.restrictedPiece}
        restrictPiece={restrictPiece}
        colorTheme={tokens.boardTheme}
        animationDuration={ANIMATION_MS}
        onPieceDropExtension={move}
        movableColor={board.movableColor}
      />
    ),
    [
      board.position,
      board.disabled,
      board.lastMoveFen,
      board.arePremovesAllowed,
      board.restrictedPiece,
      board.movableColor,
      orientation,
      tokens.boardTheme,
      move,
      restrictPiece,
    ],
  );

  return (
    <View
      style={[
        styles.frame,
        {
          paddingHorizontal: padX,
          paddingVertical: padY,
          // R6.3: the frame is the RAIL's surface, not a grey band of its own.
          // `tokens.frame` differs from `tokens.surface` in the two Gallery
          // palettes, which is what made the board sit in a visible tray.
          backgroundColor: tokens.surface,
          boxShadow: `${tokens.sh2}, ${tokens.frameEdge}`,
        } as any,
      ]}>
      <View
        style={[
          styles.board,
          {
            width: boardSize,
            height: boardSize,
            boxShadow: `inset 0 0 0 1px ${tokens.line}`,
          } as any,
        ]}>
        {boardElement}
        {browsing ? (
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={snapToLive}
            testID="v2-board-snap"
            accessibilityLabel="Back to the live position"
          />
        ) : null}
      </View>
      <BoardCountdown />
    </View>
  );
});

const styles = StyleSheet.create({
  frame: {
    // R6.1: 12 px, the same radius as the rail and the player rows
    borderRadius: RADIUS.panel,
    alignSelf: 'center',
  },
  board: {
    borderRadius: RADIUS.board,
    overflow: 'hidden',
    // the anchor the browse-back tap-catcher fills
    position: 'relative',
  },
});
